import React, {
  memo,
  useRef,
  useState,
  useCallback,
  useEffect,
  useMemo,
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
} from 'react';
import cn from 'classnames';
import { noop } from 'lodash';

import { Icon } from 'components';
import { Radio, Input } from 'components/form';
import { ButtonV3 } from 'components/ComponentV2';
import { useOnClickOutside } from 'hooks/useOnClickOutside';
import { Option } from 'lib/models/option';
import { formatToK } from 'utils/format';
import styles from './SingleSelect.module.scss';

export interface SingleSelectCommonProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  /** title */
  title: string;
  /** values */
  value: string;
  /** options */
  options?: Array<Option>;
  /** onChange handler */
  onChange: (value: string) => void;
  /** alignment */
  alignment?: 'left' | 'center' | 'right';
  /** is it searchable? */
  isSearchable?: boolean;
  /** placeholder text for search */
  placeholder?: string;
  /** custom button class */
  triggerButtonClassName?: string;
  /** dropdown class name */
  dropdownClassName?: string;
  /** close dropdown on select */
  closeOnSelect?: boolean;
  /** direction of dropdown */
  direction?: 'top' | 'bottom';
  /** disabled */
  disabled?: boolean;
  /** hide chevron icon on disabled */
  hideChevronIconOnDisabled?: boolean;
  optionLabelFirst?: boolean;
  showCheckmark?: boolean;
  showClearButton?: boolean;
  customActionButton?: React.ReactElement;
}

type MultiRefs = {
  listRef: React.RefObject<HTMLDivElement>;
  buttonRef: React.RefObject<HTMLButtonElement>;
};

type ApplyOnChangeProps = {
  changeOnShow?: false;
};

type ApplyOnShowProps = {
  changeOnShow: true;
  partialCount: number;
  partialLoading: boolean;
  partialValue: string | null;
  onPartialChange: (value?: string | null) => void;
};

type NonPartialProps = SingleSelectCommonProps & ApplyOnChangeProps;
type PartialProps = SingleSelectCommonProps & ApplyOnShowProps;
type SingleSelectProps = NonPartialProps | PartialProps;

const isApplyOnShow = (props: SingleSelectProps): props is PartialProps =>
  (props as PartialProps).changeOnShow;

const SingleSelectComponent: ForwardRefRenderFunction<
  MultiRefs,
  SingleSelectProps
> = (props, ref) => {
  const {
    className = '', // custom class name
    style, // custom style
    options,
    title,
    value,
    onChange,
    isSearchable = true,
    placeholder,
    alignment = 'left',
    triggerButtonClassName,
    dropdownClassName,
    closeOnSelect = true,
    direction = 'bottom',
    disabled,
    hideChevronIconOnDisabled,
    optionLabelFirst = false,
    showCheckmark = false,
    customActionButton,
  } = props;

  const contentClassNames = cn(styles.SingleSelect, className);
  const singleSelectRef = useRef(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const trimmedSearch = search.trim();

  useImperativeHandle(ref, () => ({
    listRef: listRef,
    buttonRef: buttonRef,
  }));

  const onClose = useCallback(() => {
    setOpen(false);

    if (open && isApplyOnShow(props)) {
      props.onPartialChange();
    }
  }, [open, props]);

  const selectedValue = isApplyOnShow(props)
    ? typeof props.partialValue !== 'undefined'
      ? props.partialValue
      : value
    : value;

  useEffect(() => {
    if (open === false) {
      setSearch('');
      buttonRef.current?.setAttribute('aria-expanded', 'false');
    } else {
      buttonRef.current?.setAttribute('aria-expanded', 'true');
    }
  }, [open]);

  const onSelect = (value: string) => {
    if (isApplyOnShow(props)) {
      props.onPartialChange(value);
    } else {
      onChange(value);
      if (closeOnSelect) onClose();
    }
  };

  const onReset = () => {
    if (isApplyOnShow(props)) {
      props.onPartialChange(null);
    }
  };

  const onShow = useCallback(() => {
    onChange(selectedValue || '');
    onClose();
  }, [selectedValue, onClose, onChange]);

  useOnClickOutside({
    ref: singleSelectRef,
    onOutsideClick: open ? (isApplyOnShow(props) ? onShow : onClose) : noop,
  });

  const filteredOptions = useMemo(() => {
    if (!options) {
      return null;
    }

    return isSearchable
      ? options.filter(
          (option) =>
            option.label.toLowerCase().indexOf(trimmedSearch.toLowerCase()) > -1
        )
      : options;
  }, [trimmedSearch, options, isSearchable]);

  const selectedValueLabel = useMemo(() => {
    return options?.find((option) => option.value === value)?.label;
  }, [options, value]);

  return (
    <div
      className={contentClassNames}
      style={style}
      data-testid="SingleSelect"
      ref={singleSelectRef}
    >
      <button
        ref={buttonRef}
        className={cn(
          styles.SingleSelectTrigger,
          {
            [styles.SingleSelectTriggerOpen]: open,
            [styles.selected]: value !== undefined,
          },
          triggerButtonClassName
        )}
        type="button"
        onClick={() => setOpen(!open)}
        disabled={disabled}
      >
        {selectedValueLabel || title}
        {!(disabled && hideChevronIconOnDisabled) && (
          <Icon
            iconName="icon_arrow-down"
            size="xsmall"
            className={cn(styles.SingleSelectIcon, {
              [styles.SingleSelectIconReverse]: open,
            })}
          />
        )}
      </button>
      {open ? (
        <div
          className={cn(styles.SingleSelectDropdown, dropdownClassName, {
            [styles.SingleSelectDropdownAlignLeft]: alignment === 'left',
            [styles.SingleSelectDropdownAlignCenter]: alignment === 'center',
            [styles.SingleSelectDropdownAlignRight]: alignment === 'right',
            [styles.SingleSelectDropdownDirectionTop]: direction === 'top',
            [styles.SingleSelectDropdownDirectionBottom]:
              direction === 'bottom',
          })}
          ref={listRef}
        >
          {isSearchable ? (
            <div className={styles.SingleSelectDropdownSearch}>
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className={styles.SingleSelectDropdownSearchInput}
                startIconClassName={styles.SingleSelectDropdownSearchIcon}
                placeholder={placeholder}
                startIcon="search"
                startIconSize="small"
                endIconSize="xsmall"
                endIcon={search ? 'cancel' : undefined}
                onEndIconClick={() => setSearch('')}
              />
            </div>
          ) : null}
          <ul className={styles.SingleSelectDropdownOptions}>
            {filteredOptions?.map((option) => {
              const { id, label } = option;
              const checked = selectedValue === option.value;
              return (
                <li
                  key={id}
                  className={cn(styles.SingleSelectDropdownOption, {
                    [styles.SingleSelectDropdownOptionSelected]: checked,
                    [styles['label-first']]: optionLabelFirst,
                  })}
                  onClick={() => onSelect(option.value)}
                >
                  {showCheckmark ? (
                    <div className={styles.SingleSelectDropdownOptionCheckmark}>
                      <span>{label}</span>
                      {checked && (
                        <Icon
                          iconName="icon_check"
                          size="xsmall"
                          className={
                            styles.SingleSelectDropdownOptionCheckmarkIcon
                          }
                        />
                      )}
                    </div>
                  ) : (
                    <Radio
                      checked={checked}
                      readOnly
                      labelFirst={optionLabelFirst}
                      className={styles.SingleSelectDropdownOptionRadio}
                    >
                      {label}
                    </Radio>
                  )}
                </li>
              );
            })}
          </ul>
          {isApplyOnShow(props) && (
            <footer
              className={cn(styles.actions, {
                [styles.hasActionButton]: customActionButton,
              })}
            >
              {customActionButton ? customActionButton : null}
              {customActionButton ? <div className={styles.space}></div> : null}
              {selectedValue ? (
                <ButtonV3 color="secondary" size="small" onClick={onReset}>
                  Reset
                </ButtonV3>
              ) : (
                <ButtonV3 color="secondary" size="small" onClick={onClose}>
                  Cancel
                </ButtonV3>
              )}
              <ButtonV3
                size="small"
                className={styles['show-button']}
                onClick={onShow}
                isLoading={props.partialLoading}
              >{`Show ${formatToK(props.partialCount)}`}</ButtonV3>
            </footer>
          )}
        </div>
      ) : null}
    </div>
  );
};

export const SingleSelect = memo(forwardRef(SingleSelectComponent));

SingleSelect.displayName = 'SingleSelect';
